import { ListedInstrument } from '@eih-netbroker/common';
import { nanoid } from 'nanoid';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as KeyboardArrowRightIcon } from '../../../../assets/icons/arrow-right-s-line.svg';
import { ReactComponent as CloseIcon } from '../../../../assets/icons/close-line.svg';
import { ReactComponent as InfoIcon } from '../../../../assets/icons/information-fill.svg';
import { useLocale } from '../../../../context/NbLocalizationContext';
import { useSnackbar } from '../../../../context/SnackbarContext';
import { NbButton } from '../../../../shared/components/button/Button';
import { FeedbackBox } from '../../../../shared/components/feedback-box/FeedbackBox';
import { NbIconButton } from '../../../../shared/components/icon-button/IconButton';
import { NbNumericFormat } from '../../../../shared/components/numeric-format/numericFormat';
import { NbSelect } from '../../../../shared/components/select/Select';
import { NbSentimentSelector } from '../../../../shared/components/sentiment-selector/SentimentSelector';
import { NbTextField } from '../../../../shared/components/text-field/TextField';
import { Config } from '../../../../shared/config/config';
import { routes } from '../../../../shared/data/routes';
import { useOptions } from '../../../../shared/services/useOptions';
import { usePriceAlerts } from '../../../../shared/services/usePriceAlerts';
import { priceAlertSchema } from '../../schemas/priceAlertSchemas';
import { PriceAlert, SentimentType, TriggerState } from '../../states/priceAlertsAtom';
import { WidgetInstrumentSearchAndDisplay } from '../WidgetInstrumentSearchAndDisplay';
import classes from './PriceAlertEditModal.module.scss';

export interface PriceAlertEditModalProps {
  onCancel: () => void;
  instrument?: ListedInstrument;
  priceAlert?: PriceAlert;
  isFromPriceAlertsWidget: boolean;
}

export const PriceAlertEditModal = ({
  onCancel,
  priceAlert,
  instrument,
  isFromPriceAlertsWidget,
}: PriceAlertEditModalProps) => {
  const { translation, translateWithParams } = useLocale();
  const { savePriceAlert, priceAlerts } = usePriceAlerts();
  const navigate = useNavigate();

  const [selectedInstrument, setSelectedInstrument] = useState<ListedInstrument | null>(
    (instrument ? instrument : priceAlert?.instrument) || null,
  );
  const [note, setNote] = useState<string>(priceAlert?.comment || '');
  const [activationPrice, setActivationPrice] = useState(priceAlert?.activationPrice);
  const [triggerState, setTriggerState] = useState(priceAlert?.condition || TriggerState.Le);
  const [sentiment, setSentiment] = useState(priceAlert?.sentiment || SentimentType.Neut);
  const [isLoading, setIsLoading] = useState(false);

  const { showError, showSuccess } = useSnackbar();
  const { triggerStateOptions } = useOptions();

  const info = useMemo(() => {
    if (!selectedInstrument || !activationPrice || !triggerState) {
      return '';
    }
    return translateWithParams(translation.priceAlerts.modal.info, {
      instrument: selectedInstrument.instrumentName,
      triggerState:
        triggerState === TriggerState.Le
          ? translation.priceAlerts.triggerStateShort.le
          : translation.priceAlerts.triggerStateShort.ge,
      price: activationPrice.toString(),
    });
  }, [selectedInstrument, activationPrice, triggerState, translation, translateWithParams]);

  const updateNote = useCallback(
    (text: string) => {
      if (text.length <= Config.NetBroker.PriceAlertCharacterMaxCount) {
        setNote(text);
      }
    },
    [setNote],
  );

  const checkIfAlertIsAlreadyPresent = useCallback(
    (
      newAlert: Pick<PriceAlert, 'sentiment' | 'condition'> & {
        instrument: ListedInstrument | null;
        activationPrice?: number;
      },
    ) => {
      return priceAlerts.priceAlerts.some(
        alert =>
          alert.instrument.isin === newAlert.instrument?.isin &&
          alert.sentiment === newAlert.sentiment &&
          alert.activationPrice === newAlert.activationPrice &&
          alert.condition === newAlert.condition &&
          alert.comment === note,
      );
    },
    [note, priceAlerts.priceAlerts],
  );

  const save = useCallback(async () => {
    const obj = {
      ...(priceAlert || {}),
      instrument: selectedInstrument,
      comment: note,
      activationPrice,
      condition: triggerState,
      sentiment,
    };

    try {
      setIsLoading(true);
      priceAlertSchema.validateSync(obj);

      if (checkIfAlertIsAlreadyPresent(obj)) {
        throw new Error(translation.priceAlerts.modal.alreadyPresent);
      }

      await savePriceAlert(obj as PriceAlert);
      showSuccess(priceAlert?.id ? translation.priceAlerts.modal.updated : translation.priceAlerts.modal.created);
      onCancel();
    } catch (err) {
      showError(err);
    } finally {
      setIsLoading(false);
    }
  }, [
    selectedInstrument,
    note,
    activationPrice,
    triggerState,
    sentiment,
    setIsLoading,
    savePriceAlert,
    priceAlert,
    onCancel,
    showError,
    showSuccess,
    translation,
    checkIfAlertIsAlreadyPresent,
  ]);

  const handleNavigate = () => {
    navigate(routes.portfolio);
    onCancel();
  };

  return (
    <div className={classes.edit}>
      <div className={classes.editHeader}>
        <h4>{translation.priceAlerts.modal.title}</h4>
        <NbIconButton
          onClick={onCancel}
          icon={<CloseIcon />}
        />
      </div>

      <div className={classes.editSearch}>
        <WidgetInstrumentSearchAndDisplay
          source={`price-alert-edit-modal-${nanoid()}`}
          instrument={selectedInstrument || null}
          updateInstrument={setSelectedInstrument}
          showPriceChanges={true}
        />
      </div>

      <div className={classes.editContent}>
        <div className={classes.editRow}>
          <p>{translation.priceAlerts.modal.stopPriceCondition}</p>
          <NbSelect
            options={triggerStateOptions}
            size={'small'}
            defaultValue={priceAlert?.condition || triggerStateOptions[1].id}
            onChange={e => setTriggerState(e.target.value as TriggerState)}
          />
        </div>

        <div className={classes.editRow}>
          <p>{translation.priceAlerts.modal.exchangeRate}</p>
          <NbNumericFormat
            value={activationPrice ?? ''}
            decimalScale={2}
            size="small"
            onValueChange={e => setActivationPrice(e.floatValue)}
            customInput={NbTextField}
          />
        </div>

        <div className={classes.editRow}>
          <p>{translation.priceAlerts.modal.sentiment}</p>
          <div className={classes.editSentimentWrapper}>
            <NbSentimentSelector
              sentiment={sentiment}
              onChange={e => setSentiment(e)}
            />
          </div>
        </div>

        <div className={classes.editRow}>
          <p>{translation.priceAlerts.modal.note}</p>
          <p className={classes.editCharCount}>
            {Config.NetBroker.PriceAlertCharacterMaxCount} {translation.priceAlerts.modal.character} /{' '}
            {Config.NetBroker.PriceAlertCharacterMaxCount - note.length}
          </p>
        </div>

        <NbTextField
          className={classes.editNote}
          placeholder={translation.priceAlerts.modal.note}
          value={note}
          onChange={e => updateNote(e.target.value)}
          multiline
          rows={3}
        />

        {info && (
          <FeedbackBox
            icon={<InfoIcon />}
            type={'info'}
            message={info}
          />
        )}

        {!priceAlert && !isFromPriceAlertsWidget && (
          <div className={classes.editManagementButton}>
            <NbButton
              variant={'text'}
              onClick={handleNavigate}
            >
              {translation.priceAlerts.modal.exchangeRateManagement}
              <KeyboardArrowRightIcon />
            </NbButton>
          </div>
        )}
      </div>

      <div className={classes.editButtons}>
        <NbButton
          color={'secondary'}
          onClick={onCancel}
        >
          {translation.general.cancel}
        </NbButton>
        <NbButton
          color={'primary'}
          onClick={save}
          isLoading={isLoading}
        >
          {translation.general.save}
        </NbButton>
      </div>
    </div>
  );
};
